import React from 'react'
import { cn } from '@/lib/utils'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  variant?: 'default' | 'primary' | 'secondary'
}

export const LoadingSpinner = ({ 
  size = 'md', 
  className,
  variant = 'default'
}: LoadingSpinnerProps) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  }

  const variantClasses = {
    default: 'text-sauna-orange',
    primary: 'text-sauna-blue',
    secondary: 'text-gray-500',
  }
  
  return (
    <div className={cn(
      'relative',
      sizeClasses[size],
      className
    )}>
      {/* Glowing brand icon container */}
      <div className="relative w-full h-full">
        {/* Warm glow effect (red/orange) */}
        <div className="absolute inset-0 rounded-full blur-md opacity-40 animate-pulse bg-gradient-to-b from-red-500/30 to-orange-500/30"></div>
        
        {/* Cold glow effect (blue) */}
        <div className="absolute inset-0 rounded-full blur-md opacity-40 animate-pulse bg-gradient-to-b from-blue-500/30 to-cyan-500/30" style={{ animationDelay: '0.5s' }}></div>
        
        {/* Main brand icon */}
        <div className={cn(
          'relative w-full h-full flex items-center justify-center',
          variantClasses[variant]
        )}>
          {/* SaunaDip Logo - Thermometer with Warm/Cold Effect */}
          <svg 
            viewBox="0 0 811 160" 
            fill="currentColor"
            className="w-full h-full"
            style={{ transform: 'scale(0.15)' }}
          >
            {/* Thermometer Icon - Warm (Red) Top Half */}
            <path d="M82.76 34.9903V79.25H74.76V34.9903C74.76 33.9983 75.1814 33.0469 75.9316 32.3455C76.6817 31.6441 77.6991 31.25 78.76 31.25C79.8209 31.25 80.8383 31.6441 81.5884 32.3455C82.3386 33.0469 82.76 33.9983 82.76 34.9903Z" fill="#FF3D00"/>
            <path d="M158.76 78.9414V79.25H92.6659V35.4297C92.6659 31.9922 91.2863 28.6956 88.8307 26.2649C86.3751 23.8343 83.0446 22.4688 79.5718 22.4688C76.099 22.4688 72.7685 23.8343 70.3128 26.2649C67.8572 28.6956 66.4777 31.9922 66.4777 35.4297V79.25H-0.23999V78.9414C-0.23999 58.0711 8.13588 38.0557 23.045 23.2982C37.9542 8.54068 58.1753 0.25 79.26 0.25C100.345 0.25 120.566 8.54068 135.475 23.2982C150.384 38.0557 158.76 58.0711 158.76 78.9414Z" fill="#FF3D00"/>
            
            {/* Thermometer Icon - Cold (Blue) Bottom Half */}
            <path d="M63.76 108.475C63.7604 105.172 64.8848 101.964 66.9537 99.3643C69.0225 96.7643 71.9164 94.9223 75.1731 94.1324V79.25H82.9991V94.3058C85.6697 95.0807 88.0685 96.5723 89.9273 98.6136C91.786 100.655 93.0312 103.165 93.5236 105.864C94.0159 108.563 93.736 111.343 92.7151 113.894C91.6942 116.445 89.9726 118.666 87.7431 120.308C85.5135 121.95 82.8639 122.948 80.0909 123.192C77.3179 123.435 74.5308 122.913 72.0416 121.685C69.5525 120.457 67.4595 118.571 65.9968 116.238C64.5342 113.905 63.7597 111.217 63.76 108.475Z" fill="#1096F5"/>
            <path d="M158.76 79.25C158.76 100.202 150.384 120.296 135.475 135.111C120.566 149.927 100.345 158.25 79.26 158.25C58.1753 158.25 37.9542 149.927 23.045 135.111C8.13588 120.296 -0.23999 100.202 -0.23999 79.25H66.4777V87.6147C62.1919 90.3438 58.9045 94.3718 57.1023 99.1024C55.3 103.833 55.0787 109.014 56.4712 113.88C57.8636 118.745 60.7958 123.035 64.8335 126.115C68.8711 129.195 73.7996 130.901 78.8892 130.981C83.9788 131.06 88.9588 129.509 93.0919 126.557C97.225 123.604 100.291 119.407 101.837 114.588C103.383 109.769 103.325 104.583 101.673 99.7984C100.022 95.0139 96.8632 90.8852 92.6659 88.0236V79.25H158.76Z" fill="#1096F5"/>
          </svg>
          
          {/* Inner warm glow */}
          <div className="absolute inset-0 rounded-full blur-sm opacity-60 animate-ping bg-gradient-to-b from-red-400/40 to-orange-400/40"></div>
          
          {/* Inner cold glow */}
          <div className="absolute inset-0 rounded-full blur-sm opacity-60 animate-ping bg-gradient-to-b from-blue-400/40 to-cyan-400/40" style={{ animationDelay: '0.3s' }}></div>
        </div>
        
        {/* Sparkle effects */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full opacity-80 animate-pulse"></div>
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full opacity-80 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-1 h-1 bg-white rounded-full opacity-80 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-1 h-1 bg-white rounded-full opacity-80 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>
    </div>
  )
}

// Loading overlay component
export const LoadingOverlay = ({ 
  isLoading, 
  children, 
  className 
}: { 
  isLoading: boolean
  children: React.ReactNode
  className?: string 
}) => {
  if (!isLoading) return <>{children}</>

  return (
    <div className={cn('relative', className)}>
      {children}
      <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
        <LoadingSpinner size="lg" />
      </div>
    </div>
  )
}

// Loading button component
export const LoadingButton = ({ 
  loading, 
  children, 
  className,
  ...props 
}: { 
  loading: boolean
  children: React.ReactNode
  className?: string 
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button 
      className={cn('relative', className)} 
      disabled={loading}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      )}
      <span className={cn(loading && 'opacity-0')}>
        {children}
      </span>
    </button>
  )
}

// Full page loading component
export const FullPageLoading = ({ 
  message = "Warming up the sauna...",
  className 
}: { 
  message?: string
  className?: string 
}) => {
  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center bg-white",
      className
    )}>
      <div className="text-center space-y-6">
        {/* Minimal spinner */}
        <div className="relative flex justify-center">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-sauna-orange animate-spin"></div>
          </div>
        </div>
        
        {/* Clean text */}
        <div className="relative">
          <p className="text-sauna-navy font-semibold text-lg tracking-wide">
            {message}
          </p>
        </div>
        
        {/* Minimal animated dots */}
        <div className="flex justify-center space-x-1">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-sauna-orange rounded-full animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// Hook for managing loading states
export const useLoadingState = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);
  
  const startLoading = () => setIsLoading(true);
  const stopLoading = () => setIsLoading(false);
  const withLoading = async (fn: () => Promise<any>) => {
    startLoading();
    try {
      await fn();
    } finally {
      stopLoading();
    }
  };
  
  return {
    isLoading,
    startLoading,
    stopLoading,
    withLoading,
  };
}; 